/**
 * Start and end of shift checklists in the job pack. The leading hand fills them in on their phone;
 * answers are saved as a draft as they go and submitted once. A submitted checklist never changes:
 * a correction is sent as a new record with a reason. Everyone else sees the status.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { Chip } from "../../components/States";
import { ApiError, isQueued } from "../../lib/api";
import { fmtDate, fmtTime, localInstant, localYmd } from "../../lib/format";
import type { ChecklistAnswer, ChecklistKind, ChecklistRecord, ChecklistStatus, PackChecklist, PackField, PackShift } from "../../lib/supabase";
import { DocketItem } from "./Docket";
import { SetupPhotos } from "../crew-messages/Messages";
import { saveChecklistDraft, submitChecklist, type AnswerInput, type Answers } from "./field-data";

export const CHECKLIST_TITLE: Record<ChecklistKind, string> = { start: "Start of shift checklist", end: "End of shift checklist" };
const STATUS_LABEL: Record<ChecklistStatus, string> = { not_started: "Not started", started: "In progress", complete: "Complete" };
export const statusChip = (s: ChecklistStatus) => <Chip tone={s === "complete" ? "green" : s === "started" ? "accent" : "primary-outline"}>{STATUS_LABEL[s]}</Chip>;

const hmNow = () => new Intl.DateTimeFormat("en-GB", { timeZone: __OPS_PORTAL__.timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
const hmOf = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat("en-GB", { timeZone: __OPS_PORTAL__.timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso)) : "");
const ymdOf = (iso: string) => localYmd(new Date(iso));
/** A time of day during (or just after) the shift: on the start date, or the next day for overnight shifts. */
function timeOnShift(shift: PackShift, hm: string): string {
  let at = localInstant(ymdOf(shift.startsAt), hm);
  if (at.getTime() < Date.parse(shift.startsAt) - 3 * 3600_000) at = new Date(at.getTime() + 86400_000);
  return at.toISOString();
}

const toInput = (items: ChecklistAnswer[]): Answers =>
  Object.fromEntries(items.map((a) => [a.id, {
    answer: a.answer, note: a.note, time: a.time, signoff: a.signoff,
    finishTimes: a.finishTimes?.map((f) => ({ crewMemberId: f.crewMemberId, finishedAt: f.finishedAt })) ?? null, text: a.text,
  }]));

export function ShiftChecklists({ shift, field, onChanged }: { shift: PackShift; field: PackField; onChanged: () => void }) {
  const kinds = (["start", "end"] as const).filter((k) => field.checklists[k]);
  if (!kinds.length) return null;
  return <>{kinds.map((k) => <ChecklistCard key={k} kind={k} shift={shift} field={field} list={field.checklists[k]!} onChanged={onChanged} />)}</>;
}

function ChecklistCard({ kind, shift, field, list: loaded, onChanged }: { kind: ChecklistKind; shift: PackShift; field: PackField; list: PackChecklist; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  // The record the submit returned, shown at once until the reloaded job pack has it.
  const [justSubmitted, setJustSubmitted] = useState<ChecklistRecord | null>(null);
  // Until the reloaded pack has this record submitted (a submit can turn the draft itself into the record, same id).
  const caughtUp = justSubmitted && loaded.current?.id === justSubmitted.id && Boolean(loaded.current.submittedAt);
  const list: PackChecklist =
    justSubmitted && !caughtUp
      ? { status: "complete", items: justSubmitted.items, current: justSubmitted, history: loaded.current?.submittedAt && loaded.current.id !== justSubmitted.id ? [loaded.current, ...loaded.history] : loaded.history }
      : loaded;
  const submitted = list.current?.submittedAt ? list.current : null;
  const canFill = field.canComplete && shift.status !== "cancelled";

  return (
    <div className="card" id={`checklist-${kind}`}>
      <div className="card__head">
        <h2 className="card__title">{CHECKLIST_TITLE[kind]}</h2>
        {statusChip(list.status)}
      </div>
      <div className="card__body stack">
        {submitted && !correcting && (
          <>
            <p className="small muted">Submitted {fmtDate(submitted.submittedAt!)}{submitted.replaces ? ` as a correction: ${submitted.correctionReason}` : ""}.</p>
            <AnswerList items={submitted.items} />
            {canFill && <button type="button" className="btn btn--outline btn--sm" onClick={() => setCorrecting(true)}>Send a correction</button>}
          </>
        )}
        {!submitted && !canFill && <p className="muted">{field.canReport ? "The leading hand fills this in." : "Filled in by the shift's leading hand on their phone."}</p>}
        {canFill && !submitted && !open && (
          <button type="button" className="btn btn--primary btn--block" onClick={() => setOpen(true)}>
            {list.status === "started" ? "Carry on with the checklist" : `Start the ${kind === "start" ? "start of shift" : "end of shift"} checklist`}
          </button>
        )}
        {canFill && ((!submitted && open) || (submitted && correcting)) && (
          <ChecklistForm
            kind={kind} shift={shift} field={field} items={submitted ? submitted.items : list.items}
            correctionOf={submitted ? submitted.id : null}
            onCancel={() => { setOpen(false); setCorrecting(false); }}
            onSubmitted={(record) => { setJustSubmitted(record); setOpen(false); setCorrecting(false); onChanged(); }}
          />
        )}
      </div>
    </div>
  );
}

function ChecklistForm({ kind, shift, field, items, correctionOf, onCancel, onSubmitted }: {
  kind: ChecklistKind; shift: PackShift; field: PackField; items: ChecklistAnswer[]; correctionOf: string | null; onCancel: () => void; onSubmitted: (record: ChecklistRecord) => void;
}) {
  const [answers, setAnswers] = useState<Answers>(() => toInput(items));
  const [reason, setReason] = useState("");
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const { notice, setNotice, fail } = useNotice();
  const dirty = useRef(false);

  // Save a draft a moment after each change (not for corrections, which are sent in one go).
  useEffect(() => {
    if (correctionOf || !dirty.current) return;
    const t = setTimeout(() => {
      setSaved("saving");
      saveChecklistDraft(shift.id, kind, answers).then(() => setSaved("saved"), (e) => setSaved(isQueued(e) ? "saved" : "failed"));
    }, 1200);
    return () => clearTimeout(t);
  }, [answers, correctionOf, kind, shift.id]);

  const set = (id: string, patch: AnswerInput) => {
    dirty.current = true;
    setProblems((p) => ({ ...p, [id]: "" }));
    setAnswers((a) => ({ ...a, [id]: { ...a[id], ...patch } }));
  };

  async function submit() {
    setBusy(true);
    setNotice(null);
    try {
      onSubmitted(await submitChecklist(shift.id, kind, answers, correctionOf ? { replaces: correctionOf, reason } : undefined));
    } catch (e) {
      if (isQueued(e)) {
        // No signal: saved on the phone. Show it as submitted; it is sent (and checked) when back online.
        const now = new Date().toISOString();
        const names = new Map(field.crew.map((m) => [m.crewMemberId, m.name]));
        onSubmitted({
          id: `saved-${now}`, bookingId: shift.id, kind, replaces: correctionOf, correctionReason: correctionOf ? reason : null, context: {},
          crewMemberId: null, profileId: null, startedAt: now, submittedAt: now, updatedAt: now,
          items: items.map((i) => {
            const a = answers[i.id] ?? {};
            return { ...i, answer: a.answer ?? null, note: a.note ?? null, time: a.time ?? null, text: a.text ?? null, signoff: a.signoff ?? null, finishTimes: a.finishTimes?.map((f) => ({ ...f, name: names.get(f.crewMemberId) ?? "" })) ?? null };
          }),
        });
        return;
      }
      if (e instanceof ApiError && e.code === "incomplete") {
        // The API lists every problem; show each against its item.
        const body = e.body?.problems as { id: string; message: string }[] | undefined;
        if (body) setProblems(Object.fromEntries(body.map((p) => [p.id, p.message])));
      }
      fail(e);
      setBusy(false);
    }
  }

  return (
    <div className="stack checklist">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {items.map((item) => (
        <Item key={item.id} item={item} value={answers[item.id] ?? {}} set={(p) => set(item.id, p)} shift={shift} field={field} problem={problems[item.id]} finishTimes={finishTimesOf(answers)} />
      ))}
      {correctionOf && (
        <label className="field">
          <span className="field__label">What does this correction change?</span>
          <input className="input input--lg" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The radio check answer was wrong" />
        </label>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn--primary btn--block" disabled={busy || (correctionOf !== null && reason.trim().length < 5)} aria-busy={busy} onClick={() => void submit()}>
          {busy ? "Submitting" : correctionOf ? "Submit the correction" : "Submit the checklist"}
        </button>
        <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={onCancel}>{correctionOf ? "Cancel" : "Close for now"}</button>
      </div>
      {!correctionOf && (
        <p className="small muted" role="status">
          {saved === "saving" ? "Saving your answers" : saved === "saved" ? "Answers saved. You can close this and come back." : saved === "failed" ? "Could not save just now. Your answers are still here; try submitting when you have signal." : "Your answers are saved as you go."}
        </p>
      )}
    </div>
  );
}

function YesNo({ value, onChange, label }: { value: "yes" | "no" | null | undefined; onChange: (v: "yes" | "no") => void; label: string }) {
  return (
    <div className="yesno" role="radiogroup" aria-label={label}>
      {(["yes", "no"] as const).map((v) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={`btn btn--sm ${value === v ? (v === "yes" ? "btn--ink" : "btn--primary") : "btn--outline"}`} onClick={() => onChange(v)}>
          {v === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

/** The finish times entered so far in any finishTimes item, by crew member (the docket starts from them). */
const finishTimesOf = (answers: Answers): Record<string, string> =>
  Object.fromEntries(Object.values(answers).flatMap((a) => (a.finishTimes ?? []).map((f) => [f.crewMemberId, f.finishedAt] as const)));

function Item({ item, value, set, shift, field, problem, finishTimes }: { item: ChecklistAnswer; value: AnswerInput; set: (p: AnswerInput) => void; shift: PackShift; field: PackField; problem?: string; finishTimes: Record<string, string> }) {
  const head = (
    <>
      <span className="checkitem__label">{item.label}{!item.required && <span className="muted"> (optional)</span>}</span>
      {item.help && <span className="small muted">{item.help}</span>}
      {item.link && <a className="btn btn--outline btn--sm" href={item.link.href} target="_blank" rel="noreferrer"><Icon name="external" size={16} /> {item.link.label ?? "Open"}</a>}
    </>
  );
  const err = problem ? <p className="small sub--primary" role="alert">{problem}</p> : null;

  if (item.kind === "signoff") {
    const s = value.signoff ?? { name: "", signature: "", hoursConfirmed: "" };
    const put = (k: keyof typeof s) => (e: { target: { value: string } }) => set({ signoff: { ...s, [k]: e.target.value } });
    return (
      <fieldset className="checkitem">
        <legend className="sr-only">{item.label}</legend>
        {head}
        <label className="field"><span className="field__label">Client's name</span><input className="input input--lg" autoComplete="off" value={s.name} onChange={put("name")} /></label>
        <label className="field"><span className="field__label">Hours they confirm</span><input className="input input--lg" value={s.hoursConfirmed} onChange={put("hoursConfirmed")} placeholder="e.g. 8pm to 4:30am" /></label>
        <label className="field"><span className="field__label">Signature: the client types their name</span><input className="input input--lg signature" autoComplete="off" value={s.signature} onChange={put("signature")} /></label>
        {err}
      </fieldset>
    );
  }

  if (item.kind === "docket") {
    return (
      <div className="checkitem">
        {head}
        <DocketItem bookingId={shift.id} shiftStart={shift.startsAt} finishTimes={finishTimes} onChanged={() => {}} />
        {err}
      </div>
    );
  }

  if (item.kind === "photos") {
    return (
      <div className="checkitem">
        {head}
        <SetupPhotos bookingId={shift.id} min={item.minPhotos} />
        {err}
      </div>
    );
  }

  if (item.kind === "finishTimes") {
    const times = new Map((value.finishTimes ?? []).map((f) => [f.crewMemberId, f.finishedAt]));
    const put = (id: string, hm: string) => {
      const next = new Map(times);
      if (hm) next.set(id, timeOnShift(shift, hm));
      else next.delete(id);
      set({ finishTimes: [...next.entries()].map(([crewMemberId, finishedAt]) => ({ crewMemberId, finishedAt })) });
    };
    return (
      <fieldset className="checkitem">
        <legend className="sr-only">{item.label}</legend>
        {head}
        {field.crew.map((m) => (
          <div key={m.crewMemberId} className="timerow">
            <span>{m.name}</span>
            <input className="input" type="time" aria-label={`Finish time for ${m.name}`} value={hmOf(times.get(m.crewMemberId))} onChange={(e) => put(m.crewMemberId, e.target.value)} />
            <button type="button" className="btn btn--outline btn--xs" onClick={() => put(m.crewMemberId, hmNow())}>Now</button>
          </div>
        ))}
        {err}
      </fieldset>
    );
  }

  if (item.kind === "notes") {
    return (
      <label className="checkitem">
        {head}
        <textarea className="input" rows={3} value={value.text ?? ""} onChange={(e) => set({ text: e.target.value })} />
        {err}
      </label>
    );
  }

  return (
    <div className="checkitem">
      {head}
      {item.kind === "permitHours" && <PermitCheck field={field} />}
      <YesNo value={value.answer} label={item.label} onChange={(answer) => set({ answer, ...(item.kind === "time" && answer === "yes" && !value.time ? { time: new Date().toISOString() } : {}) })} />
      {item.kind === "time" && value.answer === "yes" && (
        <div className="timerow">
          <span>{item.timeLabel ?? "Time"}</span>
          <input className="input" type="time" aria-label={item.timeLabel ?? "Time"} value={hmOf(value.time)} onChange={(e) => set({ time: e.target.value ? timeOnShift(shift, e.target.value) : null })} />
          <button type="button" className="btn btn--outline btn--xs" onClick={() => set({ time: new Date().toISOString() })}>Now</button>
        </div>
      )}
      {value.answer === "no" && (
        <label className="field">
          <span className="field__label">Why, or what was done about it</span>
          <textarea className="input" rows={2} value={value.note ?? ""} onChange={(e) => set({ note: e.target.value })} />
        </label>
      )}
      {err}
    </div>
  );
}

function PermitCheck({ field }: { field: PackField }) {
  const { approvedHours, withinHours } = field.permit;
  return (
    <div className="small stack" style={{ gap: 4 }}>
      {approvedHours ? <span>Approved hours: <strong>{approvedHours}</strong></span> : <span className="muted">No approved hours on the job. Check the permit.</span>}
      {withinHours === true && <span><Chip tone="green">Shift start is inside the permit hours</Chip></span>}
      {withinHours === false && <span><Chip tone="primary">Shift start is outside the permit hours</Chip></span>}
    </div>
  );
}

/** A submitted checklist, read only. */
export function AnswerList({ items }: { items: ChecklistAnswer[] }) {
  return (
    <ul className="answers">
      {items.map((a) => (
        <li key={a.id} className={a.answer === "no" ? "answers__no" : undefined}>
          <span className="strong">{a.label}</span>
          <span>{describe(a)}</span>
        </li>
      ))}
    </ul>
  );
}

function describe(a: ChecklistAnswer): string {
  switch (a.kind) {
    case "signoff":
      return a.signoff ? `${a.signoff.name}, hours ${a.signoff.hoursConfirmed}, signed "${a.signoff.signature}"` : "Not given";
    case "finishTimes":
      return a.finishTimes?.length ? a.finishTimes.map((f) => `${f.name} ${fmtTime(f.finishedAt)}`).join(", ") : "None recorded";
    case "notes":
      return a.text ?? "None";
    case "photos":
      return a.photoCount === 1 ? "1 photo" : `${a.photoCount ?? 0} photos`;
    case "docket":
      return a.docket ? `${a.docket.number}, ${a.docket.status === "awaiting_signature" ? "sent to the client to sign" : a.docket.status === "disputed" ? "signed with a dispute" : "signed"}` : "No docket";
    default: {
      const base = a.answer === "yes" ? "Yes" : a.answer === "no" ? "No" : "Not answered";
      return [base, a.time ? `at ${fmtTime(a.time)}` : "", a.note ? `(${a.note})` : ""].filter(Boolean).join(" ");
    }
  }
}
